import { Route, Switch, BrowserRouter as Router } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import 'antd/dist/antd.css';
import './theme/theme.css';
import commonCss from './theme/common.module.css';
import React, { useState, Suspense, useEffect, useCallback } from 'react';
import { notification, Button } from 'antd';
import { useVersionCheck } from './hooks/useVersionCheck';

import PageHeader from './pages/PageHeader';
import LoadingSpinner from './components/LoadingSpinner';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';

import ContextProvider from './context';
import { RecoilRoot } from 'recoil';
import RecoilNexus from 'recoil-nexus';
import { setUseWhatChange } from '@simbathesailor/use-what-changed';
import { LanguageModule } from './locales';
import { SNotification } from './services/notification';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SocketService from './background-components/SocketService';
import EventHandler from './background-components/EventHandler';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { AdminBasePath } from './pages/Admin';
import { GroupBasePath } from './pages/GroupOwner';
import { UPDATE_NOTIFICATION_KEY } from './constants';
import Home from './Home';
import Mediasoup from './pages/Mediasoup';

const AdminSwitch = React.lazy(() => import('./pages/Admin'));
const GroupOwnerSwitch = React.lazy(() => import('./pages/GroupOwner'));
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/GroupOwner/Register'));
const VerifyEmail = React.lazy(() => import('./pages/VerifyEmail'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const PersonalLogin = React.lazy(() => import('./pages/PersonalLogin'));
const DeploymentInfo = React.lazy(() => import('./pages/DeploymentInfo'));

if (process.env.NODE_ENV === 'development') {
  require('./theme/debug.css');
}
setUseWhatChange(process.env.NODE_ENV === 'development');
const language = window.localStorage.getItem('language') || 'en';

SNotification.config();

export const queryClient = new QueryClient();

const App = () => {
  const [loading, setLoading] = useState(true);
  const { updateAvailable, dismiss } = useVersionCheck();

  const handleReload = useCallback(() => {
    notification.close(UPDATE_NOTIFICATION_KEY);
    window.location.reload();
  }, []);

  const handleDismiss = useCallback(() => {
    dismiss();
    notification.close(UPDATE_NOTIFICATION_KEY);
  }, [dismiss]);

  useEffect(() => {
    if (!updateAvailable) return;

    notification.info({
      key: UPDATE_NOTIFICATION_KEY,
      message: 'A new version is available',
      description: 'The app has been updated. Reload now to get the latest version.',
      duration: 0,
      placement: 'bottomRight',
      btn: (
        <span style={{ display: 'flex', gap: 8 }}>
          <Button size="small" onClick={handleDismiss}>
            Later
          </Button>
          <Button type="primary" size="small" onClick={handleReload}>
            Reload now
          </Button>
        </span>
      ),
      onClose: handleDismiss,
    });
  }, [updateAvailable, handleReload, handleDismiss]);

  useEffect(() => {
    LanguageModule.initLanguage(language).then(() => {
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <RecoilRoot>
        <RecoilNexus />
        <Router>
          <QueryParamProvider ReactRouterRoute={Route}>
            <ContextProvider>
              <ToastContainer />
              <SocketService />
              <EventHandler />
              <div className={commonCss.container}>
                <div className={commonCss.header}>
                  <PageHeader />
                </div>
                <div className={commonCss.content}>
                  <ChunkErrorBoundary>
                    <Suspense fallback={<LoadingSpinner />}>
                      <Switch>
                        <Route path={`/mediasoup`}>
                          <Mediasoup />
                        </Route>
                        <Route exact path={`/group/register`}>
                          <Register />
                        </Route>
                        <Route path={GroupBasePath}>
                          <GroupOwnerSwitch />
                        </Route>
                        <Route path={AdminBasePath}>
                          <AdminSwitch />
                        </Route>
                        <Route path={`/login`}>
                          <Login />
                        </Route>
                        <Route exact path={`/`}>
                          <Home />
                        </Route>
                        <Route path={`/personal-login`}>
                          <PersonalLogin />
                        </Route>
                        <Route path={`/verify-group/:userId/:token/:language`}>
                          <VerifyEmail key="verify-group" />
                        </Route>
                        <Route path={`/forgot-password`}>
                          <ForgotPassword />
                        </Route>
                        <Route path={`/reset-password`}>
                          <ResetPassword />
                        </Route>
                        <Route exact path={`/deployment-info`}>
                          <DeploymentInfo />
                        </Route>
                      </Switch>
                    </Suspense>
                  </ChunkErrorBoundary>
                </div>
              </div>
            </ContextProvider>
          </QueryParamProvider>
        </Router>
      </RecoilRoot>
    </QueryClientProvider>
  );
};

export default App;
